import type { MouseEvent } from 'react';

import loadable from '@loadable/component';
import BpkIconAlert from '@skyscanner-internal/backpack-web/bpk-component-icon/lg/alert--active';
import BpkIconCalendar from '@skyscanner-internal/backpack-web/bpk-component-icon/lg/calendar';
import BpkIconCars from '@skyscanner-internal/backpack-web/bpk-component-icon/lg/cars';
import BpkIconChart from '@skyscanner-internal/backpack-web/bpk-component-icon/lg/chart';
import BpkIconCityCenter from '@skyscanner-internal/backpack-web/bpk-component-icon/lg/city-center';
import BpkIconFilter from '@skyscanner-internal/backpack-web/bpk-component-icon/lg/filter';
import BpkIconFlight from '@skyscanner-internal/backpack-web/bpk-component-icon/lg/flight';
import BpkIconFlightFlexible from '@skyscanner-internal/backpack-web/bpk-component-icon/lg/flight-flexible';
import BpkIconHotelFlexible from '@skyscanner-internal/backpack-web/bpk-component-icon/lg/hotel-flexible';
import BpkIconHotels from '@skyscanner-internal/backpack-web/bpk-component-icon/lg/hotels';
import BpkIconInsurance from '@skyscanner-internal/backpack-web/bpk-component-icon/lg/insurance';
import BpkIconLanguage from '@skyscanner-internal/backpack-web/bpk-component-icon/lg/language';
import BpkIconPriceTag from '@skyscanner-internal/backpack-web/bpk-component-icon/lg/price-tag';
import BpkIconSearch from '@skyscanner-internal/backpack-web/bpk-component-icon/lg/search';
import BpkIconThumbsUp from '@skyscanner-internal/backpack-web/bpk-component-icon/lg/thumbs-up';
import BpkLink from '@skyscanner-internal/backpack-web/bpk-component-link';
import BpkText, {
  TEXT_STYLES,
} from '@skyscanner-internal/backpack-web/bpk-component-text';
import {
  ACTION_TYPE,
  COMPONENT_NAME,
} from '@skyscanner-internal/landing-pages-shared-constants/constants';
import classNames from 'classnames';
import { GLOBAL, redirect } from 'saddlebag-browser';

import logMiniEventHelper from '../../mini-event/logMiniEventHelper';
import { anchorScroll } from '../../utils';
import withErrorBoundary from '../ErrorBoundary';
import { useMBDContext } from '../common/MBD/useMBDContext';

import ConfidenceMessagingV2 from './ConfidenceMessagingV2';

import type { ConfidenceMessagingProps } from '@skyscanner-internal/landing-pages-shared-types/types-shared/ConfidenceMessaging';

import STYLES from './ConfidenceMessaging.module.scss';

const IntersectionObserverWrapper = loadable(
  () => import('../IntersectionObserverWrapper/IntersectionObserverWrapper'),
);

type ConfidenceMessagingIcon =
  ConfidenceMessagingProps['dataList'][number]['icon'];

const LINK_TAG_PATTERN = /<link href="([^"]+)">(.*?)<\/link>/;

const runAnchorTargetAction = (
  anchorId: string,
  actions: ConfidenceMessagingProps['dataList'][number]['anchorTargetActions'],
) => {
  if (!actions?.length) {
    return;
  }

  const anchorTarget = GLOBAL.getDocument().getElementById(
    anchorId,
  ) as HTMLElement | null;
  const matchingAction = actions
    .map(({ action, selector }) => ({
      action,
      target: anchorTarget?.querySelector<HTMLElement>(selector),
    }))
    .find(({ target }) => !!target);

  if (matchingAction?.action === 'click') {
    matchingAction.target?.click();
  } else if (matchingAction?.action === 'focus') {
    matchingAction.target?.focus();
  }
};

const getIconComponent = (icon: ConfidenceMessagingIcon) => {
  switch (icon) {
    case 'flight':
      return BpkIconFlight;
    case 'cars':
      return BpkIconCars;
    case 'hotels':
      return BpkIconHotels;
    case 'calendar':
      return BpkIconCalendar;
    case 'price-tag':
      return BpkIconPriceTag;
    case 'search':
      return BpkIconSearch;
    case 'hotel-flexible':
      return BpkIconHotelFlexible;
    case 'alert':
      return BpkIconAlert;
    case 'chart':
      return BpkIconChart;
    case 'flight-flexible':
      return BpkIconFlightFlexible;
    case 'insurance':
      return BpkIconInsurance;
    case 'filter':
      return BpkIconFilter;
    case 'thumbs-up':
      return BpkIconThumbsUp;
    case 'city-center':
      return BpkIconCityCenter;
    case 'language':
      return BpkIconLanguage;
    default:
      throw Error(`Icon component not defined for icon name "${icon}"`);
  }
};

const ConfidenceMessaging = (props: ConfidenceMessagingProps) => {
  const { buildMBDParam, rawComponentName } = useMBDContext();

  if (!props?.dataList?.length) {
    return null;
  }

  const useVerticalLayout = !!props.useVerticalLayout;
  const debug = !!props.debug;

  const mbdComponentName = `${rawComponentName}_item`;

  return (
    <div
      data-tracking-element-id={rawComponentName}
      data-tracking-common-params={buildMBDParam(rawComponentName, {
        version: 'v1',
      })}
      className={classNames(STYLES.ConfidenceMessaging, {
        [STYLES.debug]: debug,
        [STYLES.vertical]: useVerticalLayout,
      })}
      data-testid="confidenceMessaging"
    >
      {props.dataList.map((item, index) => {
        const Icon = getIconComponent(item.icon);
        const tagMatch = item.description.match(LINK_TAG_PATTERN);

        const onContentClick = (event: MouseEvent) => {
          const target = event.target as HTMLElement;
          const link = target.closest('a');
          const href = link?.getAttribute('href');

          if (!link || !href) {
            return;
          }

          if (href.startsWith('#')) {
            const anchorId = href.slice(1);
            const scrolled = anchorScroll(anchorId);
            if (!scrolled && item.fallbackUrl) {
              redirect(item.fallbackUrl);
            } else if (scrolled) {
              runAnchorTargetAction(anchorId, item.anchorTargetActions);
            }
          } else if (link.target === '_blank') {
            redirect(link.href, true);
          } else {
            redirect(link.href);
          }
        };

        const renderLink = (content: string, href: string) => (
          <BpkLink
            rel="nofollow"
            data-tracking-element-id={`${rawComponentName}_item_link_${index}`}
            data-tracking-common-params={buildMBDParam(
              `${rawComponentName}_item_link`,
              {
                version: 'v1',
                link_type: item.icon,
                index: index.toString(),
              },
            )}
            href={href}
            onClick={(event: MouseEvent) => {
              if (
                event.button !== 0 ||
                event.metaKey ||
                event.ctrlKey ||
                event.shiftKey ||
                event.altKey
              ) {
                return;
              }

              event.preventDefault();
              onContentClick(event);
            }}
          >
            {content}
          </BpkLink>
        );

        const description =
          tagMatch?.index !== undefined ? (
            <>
              {item.description.slice(0, tagMatch.index)}
              {renderLink(tagMatch[2], tagMatch[1])}
              {item.description.slice(tagMatch.index + tagMatch[0].length)}
            </>
          ) : (
            item.description
          );

        return (
          <div
            data-tracking-element-id={`${mbdComponentName}_${index}`}
            data-tracking-common-params={buildMBDParam(mbdComponentName, {
              version: 'v1',
            })}
            key={item.icon}
            className={STYLES.item}
          >
            <div className={STYLES.icon}>
              <Icon />
            </div>
            <div>
              <BpkText
                className={STYLES.description}
                tagName="p"
                textStyle={TEXT_STYLES.label1}
              >
                {description}
              </BpkText>
            </div>
          </div>
        );
      })}
    </div>
  );
};

const ConfidenceMessagingWrapper = (props: ConfidenceMessagingProps) => (
  <IntersectionObserverWrapper
    onElementSeen={() =>
      logMiniEventHelper({
        action_type: ACTION_TYPE.COMPONENT_VISIBLE,
        component_name: COMPONENT_NAME.CONFIDENCE_MESSAGING,
      })
    }
  >
    {props.useV2Component ? (
      <ConfidenceMessagingV2 {...props} />
    ) : (
      <ConfidenceMessaging {...props} />
    )}
  </IntersectionObserverWrapper>
);

const ConfidenceMessagingWithErrorBoundary = withErrorBoundary(
  ConfidenceMessagingWrapper,
  'confidence-messaging-component',
);
export default ConfidenceMessagingWithErrorBoundary;
